<script lang="ts" setup>
const authStore = useAuthStore();

// `useRoute()` outside `<NuxtPage>` is Nuxt's deferred route — it only catches up once the
// incoming page component has mounted and painted. Both decisions below have to be made as
// the navigation confirms, so they read the router's own current route, which vue-router
// finalises before the page renders. Its `path` is base-stripped exactly like the middleware's
// `to.path` (the cluster prefix is the router's base — see `router.options.ts`).
const router = useRouter();
const currentRoute = computed(() => router.currentRoute.value);

// The region prompts are public-only — `isGeoPromptEligible` stands down for
// signed-in visitors, so on an authed page they could never fire.
// (unified-public mounts its own copy.) Absent authType means authenticated
// (00.auth-route-protection).
const isPublicPage = computed(() => {
  const authTypes = currentRoute.value.meta.authType ?? [];
  return authTypes.includes(AUTH_ROUTE_TYPE_NONE) || authTypes.includes(AUTH_ROUTE_TYPE_GUEST);
});

// Rendering the takeover in place of the slot is what keeps the page component from
// mounting at all — the capability gate never redirects, so this is the enforcement.
// `02.capability-route-protection` only ever records a /content path, which is why the
// takeover can be wrapped in the admin shell unconditionally.
const lockedRoute = useContentLockedRoute();
const lockedCurrentRoute = computed(() =>
  lockedRoute.value?.path === currentRoute.value.path ? lockedRoute.value : null,
);
</script>

<template>
  <div>
    <div v-if="authStore.isloggingOut" class="flex h-dvh items-center justify-center bg-background">
      <KUICoreLoaderAnyway :text="$t('general.common.loggingOut')" />
    </div>
    <div v-else>
      <UnifiedRegionGeoRouting v-if="isPublicPage" />
      <!-- LayoutAdmin top-level-awaits its nav, and the page's own <NuxtPage> boundary
           is exactly what is being withheld here, so the layout supplies one. -->
      <Suspense v-if="lockedCurrentRoute">
        <LayoutAdmin>
          <!-- Keyed on the path so a locked-to-locked hop remounts: the takeover revalidates
               once per mount, and the second lock deserves its own check. -->
          <ContentLockedState :key="lockedCurrentRoute.path" :missing="lockedCurrentRoute.missing" />
        </LayoutAdmin>
        <template #fallback>
          <KUICoreContainerCentered>
            <KUICoreLoaderAnyway :text="$t('general.common.loading')" />
          </KUICoreContainerCentered>
        </template>
      </Suspense>
      <slot v-else />
    </div>
  </div>
</template>
